import type { ReadonlyVec3 } from "@dust/world/internal";
import { useQuery } from "@tanstack/react-query";
import { useDustClient } from "../hooks/useDustClient";

export const fragmentSize = 8;

function voxelToFragmentPos([x, y, z]: ReadonlyVec3): ReadonlyVec3 {
  return [
    Math.floor(x / fragmentSize),
    Math.floor(y / fragmentSize),
    Math.floor(z / fragmentSize),
  ];
}

export function fragmentToVoxelPos([x, y, z]: ReadonlyVec3): ReadonlyVec3 {
  return [x * fragmentSize, y * fragmentSize, z * fragmentSize];
}

function getAdjacentFragments([x, y, z]: ReadonlyVec3): ReadonlyVec3[] {
  return [
    [x + 1, y, z],
    [x - 1, y, z],
    [x, y + 1, z],
    [x, y - 1, z],
    [x, y, z + 1],
    [x, y, z - 1],
  ];
}

export const cityForceFieldId =
  "0x03000001f700000091fffff9cc00000000000000000000000000000000000000";

export function useForceFieldQuery(x: number, y: number, z: number) {
  const { data: dustClient } = useDustClient();
  const fragmentPos = voxelToFragmentPos([x, y, z]);

  return useQuery({
    queryKey: ["forceField", fragmentPos[0], fragmentPos[1], fragmentPos[2]],
    queryFn: async () => {
      if (!dustClient) {
        return null;
      }

      try {
        const adjacentFragments = getAdjacentFragments(fragmentPos);

        // Fetch all adjacent force fields in parallel
        const adjacentForceFields = await Promise.all(
          adjacentFragments.map(async ([fx, fy, fz]) => {
            const adjacentVoxelPos = fragmentToVoxelPos([fx, fy, fz]);
            const forceField = await dustClient.provider.request({
              method: "getForceFieldAt",
              params: {
                x: adjacentVoxelPos[0],
                y: adjacentVoxelPos[1],
                z: adjacentVoxelPos[2],
              },
            });
            return {
              fragmentPos: [fx, fy, fz] as ReadonlyVec3,
              forceField: forceField ?? null,
            };
          }),
        );
        const adjacent = adjacentForceFields.find(
          (adjacentForceField) =>
            adjacentForceField?.forceField?.forceFieldId === cityForceFieldId,
        );

        const voxelPos = fragmentToVoxelPos(fragmentPos);
        const currentForceField = await dustClient.provider.request({
          method: "getForceFieldAt",
          params: {
            x: voxelPos[0],
            y: voxelPos[1],
            z: voxelPos[2],
          },
        });

        return {
          current: {
            fragmentPos: fragmentPos,
            forceField: currentForceField ?? null,
          },
          adjacent: adjacent ?? null,
        };
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    enabled: !!dustClient,
    refetchIntervalInBackground: true,
    refetchInterval: 1_000,
    staleTime: 0,
    gcTime: 0,
  });
}
